import { Activity, Difficulty, BootcampPage } from '~/types/bootcamp'

import { GetQuestsResponse } from '~/components/QuestPanel/type'

import { AppLanguage } from '~/types/locale'

import { BootcampPageUrlParamsType, LevelActionType, LevelPageDetailsType, VccData } from '../types/bootcamp.types'

const getLevelIds = (baseId: string, type: LevelActionType, activities: Activity[]): string[] => {
  let result = [`${baseId}#${type}`]
  if (activities && activities.length > 0) {
    activities.forEach((activity) => {
      activity.difficulties.forEach((difficulty: Difficulty, index: number) => {
        const nid = `${baseId}#activity#${activity.type}#${index + 1}`
        const activityIds = Array.from({ length: difficulty.rewards }, (_, ind) => `${nid}#${ind + 1}`)
        result = [...result, ...activityIds]
      })
    })
  }
  return result
}

export const getVccPageDetail = (
  vccModuleData: VccData['module'],
  query: unknown | BootcampPageUrlParamsType,
): BootcampPage => {
  const { key, moduleKey, action, level } = query as BootcampPageUrlParamsType
  const baseId = `${key}#${moduleKey}#${level}`
  const activities = action === LevelActionType.Why ? [] : vccModuleData?.page[0]?.activities || []
  const actionType = action === LevelActionType.Activity ? LevelActionType.How : (action as LevelActionType)
  return {
    ...getDataByAction(action, level, vccModuleData),
    levelIds: getLevelIds(baseId, actionType, activities),
  }
}

const getDataByAction = (action: string, level: string, vccModuleData: VccData['module']): BootcampPage => {
  switch (action) {
    case LevelActionType.Encyclopedia:
      return getEncyclopediaData(vccModuleData, level)
    case LevelActionType.How:
      return getHowData(vccModuleData, level)
    case LevelActionType.Why:
      return getWhyData(vccModuleData, level)
    case LevelActionType.Definition:
      return getDefinitionData(vccModuleData, level)
    case LevelActionType.Activity:
      return getActivityData(vccModuleData, level)
    default:
      return getQuizData(vccModuleData)
  }
}
const getEncyclopediaData = (vccModuleData: VccData['module'], level: string): BootcampPage => {
  const pageData = vccModuleData?.page.find((item) => item.level === level)

  return {
    valid: !!pageData,
    activities: pageData?.activities,
    asset: pageData?.assets,
    sectionName: pageData?.name,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: pageData?.metaDescriptionActivity,
    encyclopediaArticle: pageData?.encyclopediaArticle ?? null,
  }
}
const getHowData = (vccModuleData: VccData['module'], level: string): BootcampPage => {
  const pageData = vccModuleData?.page.find((item) => item.level === level && !!item.activities)

  return {
    valid: !!pageData,
    asset: pageData?.assets,
    sectionName: pageData?.name,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: pageData?.metaDescription,
    blockContent: pageData?.howContent,
    encyclopediaArticle: pageData?.encyclopediaArticle ?? null,
  }
}
const getWhyData = (vccModuleData: VccData['module'], level: string): BootcampPage => {
  return {
    valid: !!vccModuleData && level === '1',
    asset: vccModuleData?.assets,
    sectionName: vccModuleData?.whyName,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: vccModuleData?.metaDescription,
    blockContent: vccModuleData?.howContent,
    encyclopediaArticle: vccModuleData?.page[0]?.encyclopediaArticle ?? null,
  }
}
const getDefinitionData = (vccModuleData: VccData['module'], level: string): BootcampPage => {
  const pageData = vccModuleData?.page.find((item) => item.level === level && !item?.activities)

  return {
    valid: !!pageData,
    asset: pageData?.assets,
    sectionName: pageData?.name,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: pageData?.metaDescription,
    blockContent: pageData?.howContent,
    encyclopediaArticle: pageData?.encyclopediaArticle ?? null,
  }
}
const getActivityData = (vccModuleData: VccData['module'], level: string): BootcampPage => {
  const pageData = vccModuleData?.page.find((item) => item.level === level && !!item.activities)

  return {
    valid: !!pageData,
    activities: pageData?.activities,
    asset: pageData?.assets,
    sectionName: pageData?.name,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: pageData?.metaDescriptionActivity,
    encyclopediaArticle: pageData?.encyclopediaArticle ?? null,
  }
}
const getQuizData = (vccModuleData: VccData['module']): BootcampPage => {
  return {
    valid: !!vccModuleData,
    hasWhyLevel: vccModuleData?.hasWhyLevel,
    metaDescription: vccModuleData?.metaDescription,
    encyclopediaArticle: null,
  }
}

export const processVccPageData = (
  vccModuleData: VccData['module'],
  query: BootcampPageUrlParamsType,
): LevelPageDetailsType => {
  const data = getVccPageDetail(vccModuleData, query)
  const {
    asset,
    activities,
    blockContent,
    sectionName,
    valid,
    metaDescription,
    hasWhyLevel,
    encyclopediaArticle,
    levelIds,
  } = data

  return {
    activities: Array.isArray(activities) ? activities : [],
    chest: asset?.chest || {},
    content: Array.isArray(blockContent) ? blockContent : [],
    encyclopediaArticle: encyclopediaArticle || null,
    help: {
      encyclopediaPath: asset?.help?.encyclopedia || '',
      drillPath: asset?.help?.mathDrill || '',
    },
    sectionName: sectionName || '',
    usefulWords: asset?.usefulWords ? asset.usefulWords : [],
    valid: query.action === 'why' && !hasWhyLevel ? false : valid,
    metaDescription: metaDescription || '',
    levelIds,
  }
}

export const checkAvailabilityInLearningPath = (
  quests: GetQuestsResponse['currentQuests'],
  subjectKey: string,
  bootcampNo: string,
  locale: AppLanguage = AppLanguage.EN,
): boolean => {
  const sliceNo = locale === AppLanguage.EN ? 3 : 4
  return (
    quests?.some((item) => {
      const [subject, bootcamp] = item.path.split('/').slice(sliceNo)
      return subject === subjectKey && bootcamp === bootcampNo
    }) ?? false
  )
}
